import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, where, serverTimestamp, Timestamp, doc, getDoc, runTransaction } from 'firebase/firestore';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, TextField, Divider, ToggleButtonGroup, ToggleButton, Stack, IconButton, Chip, CircularProgress, Grid, FormControlLabel, Checkbox, Alert, Autocomplete } from '@mui/material';
import { trFilterOptions } from '../utils/search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import PartSelectionSection from './PartSelectionSection';
import { camParts, seramikParts, ppfParts, aracTurleri } from './PreInterviewConstants';
import { notlarToText } from './PreInterviewNotes';

const IsEmriOlustur = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [loading, setLoading] = useState(true);

    // Ön görüşmeden gelen veri
    const preInterviewData = location.state;

    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [allServices, setAllServices] = useState([]);
    const [uygulamaTurleri, setUygulamaTurleri] = useState([]);

    // Pre-interview ve appointment referansları
    const [preInterviewId, setPreInterviewId] = useState(null);
    const [appointmentId, setAppointmentId] = useState(null);

    // Uygulama verileri
    // G2/G3: paketler + gocuk YENİ girişten kalktı; state'ler yalnız ESKİ kayıtların
    // düzenlenmesinde veri korunumu için duruyor (yüklenir, aynen geri yazılır).
    const [paketlerData, setPaketlerData] = useState([]);
    const [camFilmiData, setCamFilmiData] = useState([]);
    const [ppfData, setPpfData] = useState([]);
    const [ppfFullBodyData, setPpfFullBodyData] = useState([]); // G3: [{urunId, urunAdi}] — parçasız, fiyatsız
    const [seramikData, setSeramikData] = useState([]);
    const [gocukData, setGocukData] = useState({ aktif: false, fiyat: 0, aciklama: '' });
    const [yikamaData, setYikamaData] = useState({ aktif: false, fiyat: 0 });
    const [digerHizmetler, setDigerHizmetler] = useState([{ aciklama: '' }]);

    // G1/G4: toplamTutar ELLE girilir. İskonto alanı müşteri isteğiyle tamamen kaldırıldı
    // (2026-08-30) — hiçbir kod yolu artık iskontoTutari okumaz/yazmaz.
    // kategoriTutarlari: kategori başına opsiyonel tutar — yalnız öneri + görüntüleme.
    const [toplamTutar, setToplamTutar] = useState(0);
    const [kategoriTutarlari, setKategoriTutarlari] = useState({ camFilmi: '', ppf: '', ppfFullBody: '', seramik: '', yikama: '', diger: '' });
    // Kullanıcı Toplam Tutar'a elle dokunduysa öneri onu EZMEZ ("Öneriyi kullan" butonu çıkar).
    const [toplamElleDegisti, setToplamElleDegisti] = useState(false);
    const [odenenTutar, setOdenenTutar] = useState(0);
    const [isEmriNotlari, setIsEmriNotlari] = useState('');
    const [teslimTarihi, setTeslimTarihi] = useState(null);
    // Kademeli fiyatlandırma için araç türü (ön görüşmeden taşınır, yoksa aracın 'tip' alanı, o da yoksa elle seçilir)
    const [aracTuru, setAracTuru] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Filtrelenmiş hizmetler
    const filmServices = allServices.filter(s => s.tip === 'Cam Filmi');
    const ppfServices = allServices.filter(s => s.tip === 'PPF Kaplama');
    const ppfFullBodyServices = allServices.filter(s => s.tip === 'PPF Full Body');
    const seramikServices = allServices.filter(s => s.tip === 'Seramik Kaplama');

    useEffect(() => {
        const unsub = onSnapshot(query(collection(db, "customers"), orderBy("adSoyad", "asc")), (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, []);

    useEffect(() => {
        if (!selectedCustomerId) { setVehicles([]); return; }
        const unsub = onSnapshot(query(collection(db, "vehicles"), where("musteriId", "==", selectedCustomerId)), (snapshot) => {
            setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, [selectedCustomerId]);

    useEffect(() => {
        const unsub = onSnapshot(query(collection(db, "services"), orderBy("hizmetAdi", "asc")), (snapshot) => {
            setAllServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, []);

    // Ön görüşmeden veya randevudan gelen verilerle formu doldur
    useEffect(() => {
        if ((preInterviewData?.fromPreInterview || preInterviewData?.fromAppointment) && !isEditMode) {
            // Müşteri seç (aracı müşteri yüklendikten sonra seçilecek)
            if (preInterviewData.musteriId) setSelectedCustomerId(preInterviewData.musteriId);
            if (preInterviewData.preInterviewId) setPreInterviewId(preInterviewData.preInterviewId);
            if (preInterviewData.appointmentId) setAppointmentId(preInterviewData.appointmentId);

            // Teslim tarihi - ISO string'den Date'e
            if (preInterviewData.teslimTarihi) {
                try {
                    const date = new Date(preInterviewData.teslimTarihi);
                    if (!isNaN(date.getTime())) {
                        setTeslimTarihi(date);
                    }
                } catch (e) {
                    console.error("Teslim tarihi parse hatası:", e);
                }
            }

            // Notlar (ön görüşme notu dizi formatında da gelebilir → düz metne indir)
            const gelenNotlar = notlarToText(preInterviewData.notlar);
            if (gelenNotlar) setIsEmriNotlari(gelenNotlar);

            // Araç türü (teklifteki kademeli fiyatın iş emrinde de geçerli olması için)
            if (preInterviewData.aracTuru) setAracTuru(preInterviewData.aracTuru);

            // Hizmetleri eşleştir (G2/G3: paket/göçük iş emrine "Diğer" satırı olarak taşınır — veri kaybı yok)
            const uygulamalar = [];
            const ilgilenilenHizmetler = preInterviewData.ilgilenilenHizmetler || [];
            if (ilgilenilenHizmetler.includes('Cam Filmi')) uygulamalar.push('camFilmi');
            if (ilgilenilenHizmetler.includes('PPF Kaplama')) uygulamalar.push('ppf');
            if (ilgilenilenHizmetler.includes('PPF Full Body')) uygulamalar.push('ppfFullBody');
            if (ilgilenilenHizmetler.includes('Seramik Kaplama') || ilgilenilenHizmetler.includes('Seratik Kaplama')) uygulamalar.push('seramik');
            if (ilgilenilenHizmetler.includes('Yıkama')) uygulamalar.push('yikama');
            if (ilgilenilenHizmetler.includes('Detay Temizlik') || ilgilenilenHizmetler.includes('Diğer')) uygulamalar.push('diger');

            // Uygulama verilerini doldur
            if (preInterviewData.uygulamalar) {
                if (preInterviewData.uygulamalar.camFilmi) setCamFilmiData(preInterviewData.uygulamalar.camFilmi);
                if (preInterviewData.uygulamalar.ppf) setPpfData(preInterviewData.uygulamalar.ppf);
                if (preInterviewData.uygulamalar.ppfFullBody) setPpfFullBodyData(preInterviewData.uygulamalar.ppfFullBody);
                if (preInterviewData.uygulamalar.seramik) setSeramikData(preInterviewData.uygulamalar.seramik);

                const digerSatirlari = [];

                // ESKİ ön görüşmedeki paket kalemleri "Diğer" satırına düşürülür (G2 dönüşüm eşlemesi).
                (preInterviewData.uygulamalar.paketler || []).forEach(paket => {
                    digerSatirlari.push({
                        aciklama: `Paket: ${paket.paketAdi || paket.urunAdi || 'Paket'}`,
                        fiyat: Number(paket.paketFiyat || paket.fiyat || 0)
                    });
                });

                // Ön görüşmede "basit hizmetler" (Yıkama / Göçük / Detay Temizlik / Diğer) tek listede tutulur.
                // Aktarılmazsa teklifteki bu kalemler iş emrinde kaybolur ve toplam tutar ayrışır.
                const basitler = preInterviewData.uygulamalar.diger || [];
                if (basitler.length) {
                    // ESKİ göçük kalemleri de "Diğer" satırı olur (G3: göçük kategorisi emekli).
                    const gocukKalemleri = basitler.filter(i => i.tip === 'Göçük Düzeltme');
                    const yikamaKalemleri = basitler.filter(i => i.tip === 'Yıkama');
                    const digerKalemleri = basitler.filter(i => i.tip !== 'Göçük Düzeltme' && i.tip !== 'Yıkama');

                    gocukKalemleri.forEach(i => {
                        digerSatirlari.push({
                            aciklama: i.hizmetAdi ? `Göçük: ${i.hizmetAdi}` : 'Göçük Düzeltme',
                            fiyat: Number(i.fiyat || 0)
                        });
                    });
                    if (yikamaKalemleri.length) {
                        setYikamaData({ aktif: true, fiyat: yikamaKalemleri.reduce((s, i) => s + Number(i.fiyat || 0), 0) });
                        if (!uygulamalar.includes('yikama')) uygulamalar.push('yikama');
                    }
                    digerKalemleri.forEach(i => {
                        digerSatirlari.push({
                            aciklama: i.hizmetAdi || i.tip || 'Hizmet',
                            fiyat: Number(i.fiyat || 0)
                        });
                    });
                }

                if (digerSatirlari.length) {
                    setDigerHizmetler(digerSatirlari);
                    if (!uygulamalar.includes('diger')) uygulamalar.push('diger');
                }
            }

            setUygulamaTurleri(uygulamalar);

            // Teklif toplamı iş emrine TEK TOPLAM olarak taşınır (kalem fiyat aktarımı bitti).
            if (Number(preInterviewData.toplamTutar) > 0) {
                setToplamTutar(Number(preInterviewData.toplamTutar));
                setToplamElleDegisti(true);
            }

            setLoading(false);
        }
    }, [preInterviewData, isEditMode]);

    // Araç türü: elle seçilmediyse seçili aracın 'tip' alanından türet (yalnızca yeni iş emrinde)
    useEffect(() => {
        if (isEditMode || aracTuru || !selectedVehicleId) return;
        const vehicle = vehicles.find(v => v.id === selectedVehicleId);
        if (vehicle?.tip) setAracTuru(vehicle.tip);
    }, [selectedVehicleId, vehicles, aracTuru, isEditMode]);

    // Araç seçimi - vehicles yüklendikten sonra
    useEffect(() => {
        if (preInterviewData?.aracId && vehicles.length > 0) {
            const aracMevcut = vehicles.find(v => v.id === preInterviewData.aracId);
            if (aracMevcut) {
                setSelectedVehicleId(preInterviewData.aracId);
            }
        }
    }, [vehicles, preInterviewData]);

    useEffect(() => {
        if (isEditMode) {
            setLoading(true);
            const fetchWorkOrder = async () => {
                const docRef = doc(db, "workOrders", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setSelectedCustomerId(data.musteriId);
                    setSelectedVehicleId(data.aracId);
                    setTeslimTarihi(data.teslimTarihi ? data.teslimTarihi.toDate() : null);

                    const uygulamalar = [];
                    // 'paketler'/'gocuk' toggle UI'dan kalktı ama ESKİ kayıtlarda okunur ve
                    // kaydederken aynen korunur (çift-format düzenleme — veri kaybı yok).
                    if (data.uygulamalar?.paketler?.length > 0) uygulamalar.push('paketler');
                    if (data.uygulamalar?.camFilmi?.length > 0) uygulamalar.push('camFilmi');
                    if (data.uygulamalar?.ppf?.length > 0) uygulamalar.push('ppf');
                    if (data.uygulamalar?.ppfFullBody?.length > 0) uygulamalar.push('ppfFullBody');
                    if (data.uygulamalar?.seramik?.length > 0) uygulamalar.push('seramik');
                    if (data.uygulamalar?.gocuk?.aktif) uygulamalar.push('gocuk');
                    if (data.uygulamalar?.yikama?.aktif) uygulamalar.push('yikama');
                    if (data.uygulamalar?.diger?.length > 0) uygulamalar.push('diger');
                    setUygulamaTurleri(uygulamalar);

                    setPaketlerData(data.uygulamalar?.paketler || []);
                    setCamFilmiData(data.uygulamalar?.camFilmi || []);
                    setPpfData(data.uygulamalar?.ppf || []);
                    setPpfFullBodyData(data.uygulamalar?.ppfFullBody || []);
                    setSeramikData(data.uygulamalar?.seramik || []);
                    setGocukData(data.uygulamalar?.gocuk || { aktif: false, fiyat: 0, aciklama: '' });
                    setYikamaData(data.uygulamalar?.yikama || { aktif: false, fiyat: 0 });
                    setDigerHizmetler(data.uygulamalar?.diger?.length ? data.uygulamalar.diger : [{ aciklama: '' }]);
                    setOdenenTutar(data.odenenTutar || 0);
                    setIsEmriNotlari(data.isEmriNotlari || '');
                    setPreInterviewId(data.preInterviewId || null);
                    setAracTuru(data.aracTuru || '');

                    // Çift-format düzenleme: kayıtlı toplam/kategori tutarları yüklenir;
                    // toplam elle-girilmiş sayılır ki öneri effect'i kayıtlı değeri ezmesin.
                    setToplamTutar(data.toplamTutar || 0);
                    setKategoriTutarlari({
                        camFilmi: '', ppf: '', seramik: '', yikama: '', diger: '',
                        ...Object.fromEntries(Object.entries(data.kategoriTutarlari || {}).map(([k, v]) => [k, String(v)]))
                    });
                    setToplamElleDegisti(true);
                } else {
                    alert("Düzenlenecek iş emri bulunamadı!");
                    navigate('/is-emirleri');
                }
                setLoading(false);
            };
            fetchWorkOrder();
        } else if (!preInterviewData?.fromPreInterview) {
            setLoading(false);
        }
    }, [id, isEditMode, navigate, preInterviewData]);

    // G1/G4 öneri hesabı: önerilen toplam = aktif kategorilerin tutarları (+ eski kayıttan
    // taşınan paket/kalem fiyat toplamları). Kaydedilen toplamTutar formdaki ELLE girilebilir
    // değerdir — öneri yalnız kolaylıktır.
    const num = (v) => Number(v) || 0;
    const eskiKalemToplami = (arr) => arr.reduce((sum, p) => {
        if (p.satisTipi === 'paket') return sum + (p.paketFiyat || p.fiyat || 0);
        return sum + (typeof p.fiyat === 'number' ? p.fiyat : 0);
    }, 0);
    const toplamOneri =
        (uygulamaTurleri.includes('paketler') ? eskiKalemToplami(paketlerData) : 0) +
        (uygulamaTurleri.includes('camFilmi') ? num(kategoriTutarlari.camFilmi) + eskiKalemToplami(camFilmiData) : 0) +
        (uygulamaTurleri.includes('ppf') ? num(kategoriTutarlari.ppf) + eskiKalemToplami(ppfData) : 0) +
        (uygulamaTurleri.includes('ppfFullBody') ? num(kategoriTutarlari.ppfFullBody) : 0) +
        (uygulamaTurleri.includes('seramik') ? num(kategoriTutarlari.seramik) + eskiKalemToplami(seramikData) : 0) +
        (uygulamaTurleri.includes('gocuk') ? num(gocukData.fiyat) : 0) +
        (uygulamaTurleri.includes('yikama') ? num(kategoriTutarlari.yikama) + num(yikamaData.fiyat) : 0) +
        (uygulamaTurleri.includes('diger') ? num(kategoriTutarlari.diger) + digerHizmetler.reduce((s, h) => s + num(h.fiyat), 0) : 0);

    useEffect(() => {
        if (!toplamElleDegisti) setToplamTutar(toplamOneri);
    }, [toplamOneri, toplamElleDegisti]);

    const handleUygulamaChange = (e, newUygulamalar) => { setUygulamaTurleri(newUygulamalar); };
    const handleAddDiger = () => setDigerHizmetler([...digerHizmetler, { aciklama: '' }]);
    const handleDigerChange = (index, field, value) => { const newData = [...digerHizmetler]; newData[index][field] = value; setDigerHizmetler(newData); };
    const handleRemoveDiger = (index) => { const newData = [...digerHizmetler]; newData.splice(index, 1); setDigerHizmetler(newData); };

    const handleSubmit = async () => {
        if (!selectedCustomerId || !selectedVehicleId || uygulamaTurleri.length === 0 || !teslimTarihi) {
            alert("Lütfen Müşteri, Araç, Teslim Tarihi ve en az bir Uygulama seçimi yapınız.");
            return;
        }
        const netToplam = Number(toplamTutar);
        if (toplamTutar === '' || isNaN(netToplam) || netToplam < 0) {
            alert("Lütfen geçerli bir Toplam Tutar giriniz (KDV hariç).");
            return;
        }
        const customer = customers.find(c => c.id === selectedCustomerId);
        const vehicle = vehicles.find(v => v.id === selectedVehicleId);

        const currentToplamTutar = netToplam;
        const currentOdenenTutar = Number(odenenTutar) || 0;
        const currentKalanTutar = currentToplamTutar - currentOdenenTutar;

        // Yalnız aktif ve dolu kategori tutarları yazılır (bilgi alanı — formüle girmez).
        const kategoriAktifMi = { camFilmi: 'camFilmi', ppf: 'ppf', ppfFullBody: 'ppfFullBody', seramik: 'seramik', yikama: 'yikama', diger: 'diger' };
        const temizKategoriTutarlari = {};
        Object.entries(kategoriAktifMi).forEach(([key, uygulama]) => {
            if (uygulamaTurleri.includes(uygulama) && num(kategoriTutarlari[key]) > 0) {
                temizKategoriTutarlari[key] = num(kategoriTutarlari[key]);
            }
        });

        // Ortak alanlar. odenenTutar/kalanTutar bilerek DIŞARIDA: düzenleme modunda bu iki alan
        // formdan değil, transaction içinde okunan güncel dokümandan türetilir (lost-update önlemi).
        const workOrderData = {
            musteriId: customer.id, musteriAdSoyad: customer.adSoyad, musteriTelefon: customer.telefon,
            aracId: vehicle.id, aracPlaka: vehicle.plaka || '', aracMarkaModel: `${vehicle.marka} ${vehicle.model}`,
            // G9: marka/model AYRI alanlar olarak da denormalize edilir (rapor için).
            aracMarka: vehicle.marka || '', aracModel: vehicle.model || '',
            aracTuru: aracTuru || '',
            toplamTutar: currentToplamTutar, // ANLAMI SABİT: elle girilen toplam tutar (KDV hariç)
            kategoriTutarlari: temizKategoriTutarlari,
            isEmriNotlari: (isEmriNotlari || '').toLocaleUpperCase('tr-TR'), // Faz 4.5: BÜYÜK HARF
            teslimTarihi: Timestamp.fromDate(teslimTarihi),
            preInterviewId: preInterviewId || null,
            uygulamalar: {
                // paketler/gocuk: yeni girişte üretilmez; eski kayıt düzenlemesinde AYNEN korunur (G2/G11)
                paketler: uygulamaTurleri.includes('paketler') ? paketlerData : [],
                camFilmi: uygulamaTurleri.includes('camFilmi') ? camFilmiData : [],
                ppf: uygulamaTurleri.includes('ppf') ? ppfData : [],
                ppfFullBody: uygulamaTurleri.includes('ppfFullBody') ? ppfFullBodyData : [],
                seramik: uygulamaTurleri.includes('seramik') ? seramikData : [],
                gocuk: uygulamaTurleri.includes('gocuk') ? { ...gocukData, aktif: true } : { aktif: false, fiyat: 0, aciklama: '' },
                yikama: uygulamaTurleri.includes('yikama') ? { ...yikamaData, aktif: true } : { aktif: false, fiyat: 0 },
                diger: uygulamaTurleri.includes('diger')
                    ? digerHizmetler.filter(h => h.aciklama).map(h => ({ ...h, aciklama: (h.aciklama || '').toLocaleUpperCase('tr-TR') }))
                    : [],
            }
        };

        setIsSaving(true);
        try {
            if (isEditMode) {
                const workOrderRef = doc(db, "workOrders", id);
                const customerDocRef = doc(db, "customers", customer.id);

                // Düzenleme cariyi FARK (delta) ile senkronize eder. Bu blok v1'de vardı,
                // v2 kopyalamasında kaybolmuştu (bkz. acc179b:200-228) — 2026-06-25 regresyonu.
                await runTransaction(db, async (transaction) => {
                    const workOrderDoc = await transaction.get(workOrderRef);
                    if (!workOrderDoc.exists()) throw new Error("İş emri bulunamadı!");
                    const oldWorkOrderData = workOrderDoc.data();

                    const customerDoc = await transaction.get(customerDocRef);
                    if (!customerDoc.exists()) throw new Error("Müşteri belgesi bulunamadı!");

                    // Müşteri değiştirilmiş olabilir: eski müşterinin dokümanı da okunmalı (tüm okumalar yazmalardan önce)
                    const musteriDegisti = oldWorkOrderData.musteriId && oldWorkOrderData.musteriId !== customer.id;
                    const oldCustomerRef = musteriDegisti ? doc(db, "customers", oldWorkOrderData.musteriId) : null;
                    const oldCustomerDoc = oldCustomerRef ? await transaction.get(oldCustomerRef) : null;

                    // Ödenen tutar YALNIZCA payments akışından değişir; formdaki değer yok sayılır.
                    const guncelOdenen = oldWorkOrderData.odenenTutar || 0;
                    const yeniKalan = currentToplamTutar - guncelOdenen;

                    if (musteriDegisti) {
                        // Eski müşteriden iş emrinin TAMAMINI geri al, yeni müşteriye tamamını ekle.
                        // (Eski müşteri dokümanı silinmişse geri alınacak yer yoktur; yeni müşteriye tam tutar eklenir.)
                        if (oldCustomerDoc?.exists()) {
                            const oldCust = oldCustomerDoc.data();
                            transaction.update(oldCustomerRef, {
                                toplamSatis: (oldCust.toplamSatis || 0) - (oldWorkOrderData.toplamTutar || 0),
                                toplamOdeme: (oldCust.toplamOdeme || 0) - (oldWorkOrderData.odenenTutar || 0),
                                toplamBakiye: (oldCust.toplamBakiye || 0) - (oldWorkOrderData.kalanTutar || 0)
                            });
                        }
                        const newCust = customerDoc.data();
                        transaction.update(customerDocRef, {
                            toplamSatis: (newCust.toplamSatis || 0) + currentToplamTutar,
                            toplamOdeme: (newCust.toplamOdeme || 0) + guncelOdenen,
                            toplamBakiye: (newCust.toplamBakiye || 0) + yeniKalan
                        });
                    } else {
                        const customerData = customerDoc.data();
                        const toplamFark = currentToplamTutar - (oldWorkOrderData.toplamTutar || 0);
                        const odenenFark = guncelOdenen - (oldWorkOrderData.odenenTutar || 0); // her zaman 0, açıklık için duruyor
                        const kalanFark = yeniKalan - (oldWorkOrderData.kalanTutar || 0);

                        transaction.update(customerDocRef, {
                            toplamSatis: (customerData.toplamSatis || 0) + toplamFark,
                            toplamOdeme: (customerData.toplamOdeme || 0) + odenenFark,
                            toplamBakiye: (customerData.toplamBakiye || 0) + kalanFark
                        });
                    }

                    transaction.update(workOrderRef, {
                        ...workOrderData,
                        odenenTutar: guncelOdenen,
                        kalanTutar: yeniKalan
                    });
                });

                alert("İş emri güncellendi ve müşteri bakiyesi senkronize edildi.");
                navigate(`/is-emri/${id}`);
            } else {
                const customerDocRef = doc(db, "customers", customer.id);
                const workOrdersColRef = collection(db, "workOrders");

                await runTransaction(db, async (transaction) => {
                    const customerDoc = await transaction.get(customerDocRef);
                    if (!customerDoc.exists()) {
                        throw new Error("Müşteri belgesi bulunamadı!");
                    }

                    // Mükerrer iş emri koruması: randevu transaction İÇİNDE okunur.
                    // Çift tıklama veya geri tuşuyla forma dönüş, aynı randevudan
                    // ikinci bir iş emri açıp cariyi iki kez şişirebiliyordu.
                    // (Tüm okumalar yazmalardan önce yapılmalı.)
                    let appointmentRef = null;
                    let appointmentCustomerId = null;
                    if (appointmentId) {
                        const candidateRef = doc(db, "appointments", appointmentId);
                        const appointmentDoc = await transaction.get(candidateRef);
                        if (appointmentDoc.exists()) {
                            if (appointmentDoc.data().workOrderId) {
                                throw new Error("Bu randevudan zaten bir iş emri oluşturulmuş. Randevu detayından mevcut iş emrine gidin.");
                            }
                            appointmentRef = candidateRef;
                            appointmentCustomerId = appointmentDoc.data().customerId || null;
                        }
                    }

                    const customerData = customerDoc.data();

                    const newToplamSatis = (customerData.toplamSatis || 0) + currentToplamTutar;
                    const newToplamOdeme = (customerData.toplamOdeme || 0) + currentOdenenTutar;
                    const newToplamBakiye = (customerData.toplamBakiye || 0) + currentKalanTutar;

                    transaction.update(customerDocRef, {
                        toplamSatis: newToplamSatis,
                        toplamOdeme: newToplamOdeme,
                        toplamBakiye: newToplamBakiye
                    });

                    const newWorkOrderRef = doc(workOrdersColRef);
                    const newWorkOrderData = {
                        ...workOrderData,
                        odenenTutar: currentOdenenTutar,
                        kalanTutar: currentKalanTutar,
                        appointmentId: appointmentId || null,
                        durum: "İşlemde",
                        isEmriTarihi: serverTimestamp()
                    };
                    transaction.set(newWorkOrderRef, newWorkOrderData);

                    // Peşinat, ödemeler tablosunda görünsün diye payments alt-koleksiyonuna da yazılır.
                    // (Aksi halde personel peşinatı kaydedilmemiş sanıp ikinci kez giriyordu -> çift tahsilat.)
                    if (currentOdenenTutar > 0) {
                        const pesinatRef = doc(collection(newWorkOrderRef, "payments"));
                        transaction.set(pesinatRef, {
                            tutar: currentOdenenTutar,
                            odemeYontemi: 'Nakit',
                            aciklama: 'Peşinat (iş emri açılışı)',
                            tarih: serverTimestamp(),
                            musteriId: customer.id,
                            musteriAdSoyad: customer.adSoyad,
                            aracPlaka: vehicle.plaka || ''
                        });
                    }

                    // Randevu durumunu güncelle (referans yukarıda okundu).
                    // Müşteri ön görüşme anında henüz yoksa randevu customerId'siz kaydediliyor;
                    // iş emrinde müşteri kesinleştiği için bu alan burada geriye dönük doldurulur.
                    if (appointmentRef) {
                        const appointmentGuncelleme = {
                            durum: 'Onaylandı',
                            workOrderId: newWorkOrderRef.id
                        };
                        if (!appointmentCustomerId) appointmentGuncelleme.customerId = customer.id;
                        transaction.update(appointmentRef, appointmentGuncelleme);
                    }

                    // Ön görüşme durumunu güncelle
                    if (preInterviewId) {
                        const preRef = doc(db, "preInterviews", preInterviewId);
                        transaction.update(preRef, { durum: 'Müşteriye Dönüştü' });
                    }
                });

                alert("İş emri oluşturuldu ve müşteri bakiyesi güncellendi.");
                // replace: geri tuşu doldurulmuş formu yeniden açmasın (mükerrer kayıt riski).
                navigate('/garaj', { replace: true });
            }
        } catch (e) {
            console.error("İş emri kaydedilirken hata oluştu: ", e);
            alert(`Bir hata oluştu: ${e?.message || e}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom>{isEditMode ? 'İş Emrini Düzenle' : 'Yeni İş Emri Oluştur'}</Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2, mb: 3 }}>
                {/* G8: yüzlerce kayıtta Select kullanışsızdı — Autocomplete + Türkçe filtre.
                    Boş odakta tüm listeyi AÇMAZ: en az 1 karakter yazılınca filtrelenir. */}
                <Autocomplete
                    fullWidth
                    options={customers}
                    value={customers.find(c => c.id === selectedCustomerId) || null}
                    onChange={(e, secilen) => { setSelectedCustomerId(secilen?.id || ''); setSelectedVehicleId(''); }}
                    getOptionLabel={(c) => c?.adSoyad ? `${c.adSoyad}${c.telefon ? ` (${c.telefon})` : ''}` : ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    filterOptions={(options, state) => state.inputValue.trim() ? trFilterOptions(options, state) : []}
                    openOnFocus={false}
                    noOptionsText="Aramak için ad veya telefon yazın"
                    renderInput={(params) => <TextField {...params} label="Müşteri *" placeholder="Ad veya telefon yazın..." />}
                />
                <Autocomplete
                    fullWidth
                    disabled={!selectedCustomerId}
                    options={vehicles}
                    value={vehicles.find(v => v.id === selectedVehicleId) || null}
                    onChange={(e, secilen) => setSelectedVehicleId(secilen?.id || '')}
                    getOptionLabel={(v) => v?.plaka ? `${v.plaka}${v.marka ? ` — ${v.marka} ${v.model || ''}` : ''}`.trim() : (v?.plakaBekleniyor ? 'PLAKA BEKLENİYOR' : '')}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    filterOptions={trFilterOptions}
                    noOptionsText="Bu müşterinin aracı yok"
                    renderInput={(params) => <TextField {...params} label="Araç *" placeholder="Plaka veya marka..." />}
                />
                <FormControl fullWidth sx={{ minWidth: 180 }}>
                    <InputLabel>Araç Türü</InputLabel>
                    <Select
                        value={aracTuru}
                        label="Araç Türü"
                        onChange={(e) => setAracTuru(e.target.value)}
                        disabled={isEditMode}
                    >
                        <MenuItem value=""><em>Belirtilmedi (taban fiyat)</em></MenuItem>
                        {aracTurleri.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                    </Select>
                </FormControl>
                <DatePicker label="Teslim Tarihi *" value={teslimTarihi} onChange={setTeslimTarihi} sx={{ minWidth: 220 }} />
            </Box>
            {isEditMode && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -2, mb: 2 }}>
                    Araç türü düzenlemede kilitlidir: değiştirilmesi kayıtlı kalem fiyatlarını geriye dönük değiştirirdi.
                </Typography>
            )}

            <Divider sx={{ my: 3 }}><Chip label="Uygulama Seçimi" /></Divider>

            <Stack direction="row" sx={{ overflowX: 'auto', pb: 1, mb: 3, justifyContent: 'center' }}>
                <ToggleButtonGroup
                    color="primary"
                    value={uygulamaTurleri}
                    onChange={handleUygulamaChange}
                    sx={{ justifyContent: 'center' }}
                >
                    {/* G2/G3: Paketler + Göçük butonları emekli; PPF Full Body eklendi */}
                    <ToggleButton value="camFilmi" sx={{ whiteSpace: 'nowrap' }}>Cam Filmi</ToggleButton>
                    <ToggleButton value="ppf" sx={{ whiteSpace: 'nowrap' }}>PPF</ToggleButton>
                    <ToggleButton value="ppfFullBody" sx={{ whiteSpace: 'nowrap' }}>PPF Full Body</ToggleButton>
                    <ToggleButton value="seramik" sx={{ whiteSpace: 'nowrap' }}>Seramik</ToggleButton>
                    <ToggleButton value="yikama" sx={{ whiteSpace: 'nowrap' }}>Yıkama</ToggleButton>
                    <ToggleButton value="diger" sx={{ whiteSpace: 'nowrap' }}>Diğer</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {isEditMode && (uygulamaTurleri.includes('paketler') || uygulamaTurleri.includes('gocuk')) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Bu iş emrinde eski formatta {uygulamaTurleri.includes('paketler') ? 'Paket' : ''}
                    {uygulamaTurleri.includes('paketler') && uygulamaTurleri.includes('gocuk') ? ' ve ' : ''}
                    {uygulamaTurleri.includes('gocuk') ? 'Göçük Düzeltme' : ''} kalemleri var; kaydettiğinizde
                    aynen korunur (yeni girişten kaldırıldılar).
                </Alert>
            )}

            <Stack spacing={2}>
                {/* vehicleType YALNIZCA yeni iş emrinde geçilir: düzenlemede geçilirse kayıtlı fiyatlar sessizce yeniden hesaplanır. */}
                {uygulamaTurleri.includes('camFilmi') && <PartSelectionSection title="Cam Filmi Detayları" parts={camParts} services={filmServices} data={camFilmiData} setData={setCamFilmiData} vehicleType={isEditMode ? undefined : aracTuru} category="camFilmi" kategoriTutar={kategoriTutarlari.camFilmi} onKategoriTutarChange={(v) => setKategoriTutarlari(prev => ({ ...prev, camFilmi: v }))} />}
                {uygulamaTurleri.includes('ppf') && <PartSelectionSection title="PPF Kaplama Detayları" parts={ppfParts} services={ppfServices} data={ppfData} setData={setPpfData} vehicleType={isEditMode ? undefined : aracTuru} category="ppf" kategoriTutar={kategoriTutarlari.ppf} onKategoriTutarChange={(v) => setKategoriTutarlari(prev => ({ ...prev, ppf: v }))} />}
                {uygulamaTurleri.includes('ppfFullBody') && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>PPF Full Body — Ürün Seçimi</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Tüm gövde uygulaması: parça seçimi yoktur, yalnız film ürünü seçilir.
                            Ürünler Ayarlar &gt; Hizmetler'den "PPF Full Body" tipiyle eklenir.
                        </Typography>
                        {ppfFullBodyServices.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                                Henüz PPF Full Body ürünü tanımlı değil. Ayarlar &gt; Hizmetler'den ekleyin.
                            </Typography>
                        ) : (
                            <Grid container spacing={1}>
                                {ppfFullBodyServices.map(s => {
                                    const secili = ppfFullBodyData.some(i => i.urunId === s.id);
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={s.id}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={secili}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setPpfFullBodyData(prev => [...prev, { urunId: s.id, urunAdi: s.hizmetAdi }]);
                                                            } else {
                                                                setPpfFullBodyData(prev => prev.filter(i => i.urunId !== s.id));
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={s.hizmetAdi}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                        <Divider sx={{ my: 2 }} />
                        <TextField
                            label="Kategori Tutarı (₺) — opsiyonel"
                            size="small"
                            type="number"
                            value={kategoriTutarlari.ppfFullBody}
                            onChange={(e) => setKategoriTutarlari(prev => ({ ...prev, ppfFullBody: e.target.value }))}
                            sx={{ maxWidth: 320 }}
                            fullWidth
                        />
                    </Paper>
                )}
                {uygulamaTurleri.includes('seramik') && <PartSelectionSection title="Seramik Kaplama Detayları" parts={seramikParts} services={seramikServices} data={seramikData} setData={setSeramikData} vehicleType={isEditMode ? undefined : aracTuru} category="seramik" kategoriTutar={kategoriTutarlari.seramik} onKategoriTutarChange={(v) => setKategoriTutarlari(prev => ({ ...prev, seramik: v }))} />}

                {uygulamaTurleri.includes('yikama') && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Yıkama Hizmeti</Typography>
                        <TextField label="Yıkama Tutarı (₺) — opsiyonel" type="number" size="small" value={kategoriTutarlari.yikama} onChange={e => setKategoriTutarlari(prev => ({ ...prev, yikama: e.target.value }))} sx={{ mt: 2, maxWidth: 320 }} fullWidth />
                        {Number(yikamaData.fiyat) > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Eski kayıt yıkama fiyatı: {Number(yikamaData.fiyat).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                            </Typography>
                        )}
                    </Paper>
                )}
                {uygulamaTurleri.includes('diger') && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Diğer Hizmetler</Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {digerHizmetler.map((h, i) => (
                                <Grid container spacing={1} key={i} alignItems="center">
                                    <Grid item xs><TextField label="Açıklama" size="small" fullWidth value={h.aciklama} onChange={e => handleDigerChange(i, 'aciklama', e.target.value)} /></Grid>
                                    {Number(h.fiyat) > 0 && (
                                        <Grid item xs="auto">
                                            <Typography variant="caption" color="text.secondary">
                                                Eski kayıt fiyatı: {Number(h.fiyat).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                            </Typography>
                                        </Grid>
                                    )}
                                    <Grid item xs="auto"><IconButton disabled={digerHizmetler.length === 1} onClick={() => handleRemoveDiger(i)}><DeleteIcon /></IconButton></Grid>
                                </Grid>
                            ))}
                        </Stack>
                        <Button onClick={handleAddDiger} size="small" sx={{ mt: 1 }}>+ Satır Ekle</Button>
                        <TextField label="Diğer Toplam Tutarı (₺) — opsiyonel" type="number" size="small" value={kategoriTutarlari.diger} onChange={e => setKategoriTutarlari(prev => ({ ...prev, diger: e.target.value }))} sx={{ mt: 2, maxWidth: 320, display: 'block' }} />
                    </Paper>
                )}
            </Stack>

            <Divider sx={{ my: 3 }}><Chip label="Ödeme ve Notlar" /></Divider>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}><TextField label="İş Emri Notları" multiline rows={4} fullWidth value={isEmriNotlari} onChange={e => setIsEmriNotlari(e.target.value)} /></Grid>
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                    Kategori tutarları toplamı: <b>{toplamOneri.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</b>
                                </Typography>
                                {toplamElleDegisti && toplamOneri > 0 && Number(toplamTutar) !== toplamOneri && (
                                    <Button size="small" variant="outlined" onClick={() => { setToplamTutar(toplamOneri); setToplamElleDegisti(false); }}>
                                        Öneriyi kullan
                                    </Button>
                                )}
                            </Stack>
                        </Paper>
                        <TextField
                            label="Toplam Tutar (₺) — KDV hariç"
                            type="number"
                            value={toplamTutar}
                            onChange={e => { setToplamTutar(e.target.value); setToplamElleDegisti(true); }}
                            fullWidth
                            required
                            helperText="Elle girilebilir. Kategori tutarları girildiyse öneri otomatik dolar; üzerine yazabilirsiniz."
                        />
                        <TextField
                            label={isEditMode ? 'Ödenen Tutar (₺) — salt okunur' : 'Peşinat / Ödenen Tutar (₺)'}
                            type="number"
                            value={odenenTutar}
                            onChange={e => setOdenenTutar(Number(e.target.value))}
                            InputProps={{ readOnly: isEditMode }}
                            disabled={isEditMode}
                            helperText={isEditMode
                                ? 'Ödeme yalnızca İş Emri Detay > Ödeme Ekle üzerinden girilir.'
                                : 'Girilen tutar ödemeler tablosuna "Peşinat" olarak da kaydedilir.'}
                        />
                        <TextField label="Kalan Tutar (₺)" value={((Number(toplamTutar) || 0) - (Number(odenenTutar) || 0)).toFixed(2)} fullWidth InputProps={{ readOnly: true }} />
                    </Stack>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="success" size="large" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Değişiklikleri Kaydet' : 'İş Emrini Kaydet')}
                </Button>
            </Box>
        </Paper>
    );
};

export default IsEmriOlustur;